import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { queryClient } from './lib/query-client';
import { useSession } from './lib/auth-client';
// Pages d'entrée (non authentifiées) + fallback : chargées en statique,
// pour qu'elles s'affichent instantanément sans aller chercher tout le reste.
import LoginPage from './pages/login';
import InvitePage from './pages/invite';
import NotFoundPage from './pages/not-found';
// Pages authentifiées : chargées à la demande (lazy) pour alléger
// le bundle initial (login/invite ne tirent plus dashboard, recharts, etc.).
const CabinetDashboardPage = lazy(() => import('./pages/cabinet-dashboard'));
const ContactsPage = lazy(() => import('./pages/contacts'));
const ContactDetailPage = lazy(() => import('./pages/contact-detail'));
const ContactQuotePage = lazy(() => import('./pages/contact-quote'));
const SettingsPage = lazy(() => import('./pages/settings'));
const CabinetInfoPage = lazy(() => import('./pages/cabinet-info'));
const UsersPage = lazy(() => import('./pages/users'));
const TeamsPage = lazy(() => import('./pages/teams'));
const CustomFieldsPage = lazy(() => import('./pages/custom-fields'));
const ImportsPage = lazy(() => import('./pages/imports'));
const ExportsPage = lazy(() => import('./pages/exports'));
const NotificationsPage = lazy(() => import('./pages/notifications'));
const MailInboxPage = lazy(() => import('./pages/mail-inbox'));
const MailConfigPage = lazy(() => import('./pages/mail-config'));
const MotoDevisPage = lazy(() => import('./pages/moto-devis'));
const VspDevisPage = lazy(() => import('./pages/vsp-devis'));
const PnoDevisPage = lazy(() => import('./pages/pno-devis'));
const MrhDevisPage = lazy(() => import('./pages/mrh-devis'));
const SanteDevisPage = lazy(() => import('./pages/sante-devis'));
const AutoDevisPage = lazy(() => import('./pages/auto-devis'));
const AnimalDevisPage = lazy(() => import('./pages/animal-devis'));
const ExpatrieDevisPage = lazy(() => import('./pages/expatrie-devis'));
const EmprunteurDevisPage = lazy(() => import('./pages/emprunteur-devis'));
const PrevoyanceDevisPage = lazy(() => import('./pages/prevoyance-devis'));
const ContractsPage = lazy(() => import('./pages/contracts'));
const DevisPage = lazy(() => import('./pages/devis'));
const AnnuairePage = lazy(() => import('./pages/annuaire'));
const PowerDialerPage = lazy(() => import('./pages/power-dialer'));
const ConformitePage = lazy(() => import('./pages/conformite'));
const EditiquePage = lazy(() => import('./pages/editique'));
const TachesPage = lazy(() => import('./pages/taches'));
const OpportunitesPage = lazy(() => import('./pages/opportunites'));
const TicketsPage = lazy(() => import('./pages/tickets-page'));
const ResiliationsPage = lazy(() => import('./pages/resiliations'));
const CommissionClawbacksPage = lazy(() => import('./pages/commission-clawbacks'));
const ResiliationDetailPage = lazy(() => import('./pages/resiliation-detail'));
const ResiliationNewPage = lazy(() => import('./pages/resiliation-new'));
import { CommandPalette } from './components/command-palette';
import { MeshBackground } from './components/mesh-background';
import { MobileMenuProvider } from './lib/mobile-menu-context';
import { PermissionGate } from './components/permission-gate';
import { ErrorBoundary } from './components/error-boundary';

const MAIL_PERMS = ['mail.view', 'mail.send', 'mail.config'];
const RESILIATIONS_PERMS = ['resiliations.view_all', 'resiliations.view_own', 'resiliations.view_team'];
const TACHES_PERMS = ['taches.view', 'taches.view_own'];
const TICKETS_PERMS = ['tickets.view', 'tickets.view_own'];
const DEVIS_VIEW_PERMS = ['devis.view', 'devis.detail', 'devis.create'];

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-ink-muted">Chargement...</p>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MeshBackground />
      <BrowserRouter>
        <MobileMenuProvider>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              color: '#09090b',
              border: '1px solid #e4e4e7',
            },
            className: 'toast',
          }}
        />
        <CommandPalette />
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              <p className="text-ink-muted">Chargement...</p>
            </div>
          }
        >
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite" element={<InvitePage />} />

          {/* Dashboard */}
          <Route path="/dashboard" element={<ProtectedRoute><CabinetDashboardPage /></ProtectedRoute>} />

          {/* Contacts */}
          <Route path="/contacts" element={<ProtectedRoute><PermissionGate anyOf={['contacts.view']}><ContactsPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/contacts/:contactId" element={<ProtectedRoute><PermissionGate anyOf={['contacts.detail', 'contacts.view']}><ContactDetailPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/contacts/:contactId/devis/:quoteId?" element={<ProtectedRoute><PermissionGate anyOf={DEVIS_VIEW_PERMS}><ContactQuotePage /></PermissionGate></ProtectedRoute>} />

          {/* Devis & Contrats */}
          <Route path="/devis" element={<ProtectedRoute><PermissionGate anyOf={['devis.view']}><DevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/contrats" element={<ProtectedRoute><PermissionGate anyOf={['contrats.view']}><ContractsPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/commissions/reprises" element={<ProtectedRoute><PermissionGate anyOf={['commissions.reprises_view', 'commissions.reprises_manage', 'commissions.bordereaux_import']}><CommissionClawbacksPage /></PermissionGate></ProtectedRoute>} />

          {/* Tâches & Tickets */}
          <Route path="/taches" element={<ProtectedRoute><PermissionGate anyOf={TACHES_PERMS}><TachesPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/opportunites" element={<ProtectedRoute><PermissionGate anyOf={['contacts.view_all', 'contacts.view_team', 'contacts.view_own']}><OpportunitesPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/tickets" element={<ProtectedRoute><PermissionGate anyOf={TICKETS_PERMS}><TicketsPage /></PermissionGate></ProtectedRoute>} />

          {/* Résiliation */}
          <Route path="/resiliations" element={<ProtectedRoute><PermissionGate anyOf={RESILIATIONS_PERMS}><ResiliationsPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/resiliations/nouvelle" element={<ProtectedRoute><PermissionGate anyOf={['resiliations.create']}><ResiliationNewPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/resiliations/:id" element={<ProtectedRoute><PermissionGate anyOf={RESILIATIONS_PERMS}><ResiliationDetailPage /></PermissionGate></ProtectedRoute>} />

          {/* Annuaire & Éditique */}
          <Route path="/annuaire" element={<ProtectedRoute><PermissionGate anyOf={['annuaire.view']}><AnnuairePage /></PermissionGate></ProtectedRoute>} />
          <Route path="/power-dialer" element={<ProtectedRoute><PermissionGate anyOf={['dialer.view', 'dialer.manage']}><PowerDialerPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/power-dialer/:listId" element={<ProtectedRoute><PermissionGate anyOf={['dialer.view', 'dialer.manage']}><PowerDialerPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/conformite" element={<ProtectedRoute><PermissionGate anyOf={['compliance.view_lcbft']}><ConformitePage /></PermissionGate></ProtectedRoute>} />
          <Route path="/editique" element={<ProtectedRoute><PermissionGate anyOf={['editique.view']}><EditiquePage /></PermissionGate></ProtectedRoute>} />

          {/* Formulaires devis (création) */}
          <Route path="/devis/moto" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><MotoDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/vsp" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><VspDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/pno" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><PnoDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/mrh" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><MrhDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/sante" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><SanteDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/auto" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><AutoDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/animal" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><AnimalDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/expatrie" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><ExpatrieDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/emprunteur" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><EmprunteurDevisPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/devis/prevoyance" element={<ProtectedRoute><PermissionGate anyOf={['devis.create']}><PrevoyanceDevisPage /></PermissionGate></ProtectedRoute>} />

          {/* Paramètres — le hub /parametres filtre déjà ses cartes selon les permissions */}
          <Route path="/parametres" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/parametres/cabinet" element={<ProtectedRoute><PermissionGate anyOf={['parametres.cabinet', 'parametres.view']}><CabinetInfoPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/parametres/utilisateurs" element={<ProtectedRoute><PermissionGate anyOf={['parametres.users.view', 'parametres.users.manage']}><UsersPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/parametres/utilisateurs/equipes" element={<ProtectedRoute><PermissionGate anyOf={['parametres.teams.view', 'parametres.teams.manage']}><TeamsPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/parametres/champs" element={<ProtectedRoute><PermissionGate anyOf={['parametres.champs']}><CustomFieldsPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/parametres/imports" element={<ProtectedRoute><PermissionGate anyOf={['contacts.import']}><ImportsPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/parametres/exports" element={<ProtectedRoute><PermissionGate anyOf={['contacts.export']}><ExportsPage /></PermissionGate></ProtectedRoute>} />

          {/* Notifications */}
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

          {/* Mailbox */}
          <Route path="/mail" element={<ProtectedRoute><PermissionGate anyOf={MAIL_PERMS}><MailInboxPage /></PermissionGate></ProtectedRoute>} />
          <Route path="/parametres/mail-config" element={<ProtectedRoute><PermissionGate anyOf={['mail.config']}><MailConfigPage /></PermissionGate></ProtectedRoute>} />

          {/* Redirections */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
        </Suspense>
        </MobileMenuProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
